import React, { useEffect, useState } from 'react';
import AdminLayout from '../components/Layout/AdminLayout';
import { FaSearch } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify'; 
import { Lead, User } from '../api/types'; 
import { getSubordinatesByParent, assignLeads } from '../api/api'; 

const HotLeadsPage: React.FC = () => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedSource, setSelectedSource] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [filteredLeads, setFilteredLeads] = useState<Lead[]>([]);

  // Assignment States
  const [selectedLeadIds, setSelectedLeadIds] = useState<string[]>([]);
  const [subordinates, setSubordinates] = useState<User[]>([]);
  const [assignToUserId, setAssignToUserId] = useState<string>('');

  const navigate = useNavigate();

  // --- Theme Helpers ---
  // This replaces your hardcoded .input-field CSS so it works in both Light & Dark
  const inputClass = `w-full p-2.5 rounded border outline-none transition-colors duration-300 
  bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 placeholder-gray-400
  dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:focus:border-blue-500 dark:placeholder-gray-500
  disabled:opacity-60 disabled:cursor-not-allowed`;

  const roleColorMap: Record<string, string> = {
  SUPER_ADMIN:
    "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300",
  ADMIN:
    "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300",
  MANAGER:
    "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  TEAM_LEAD:
    "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300",
  AGENT:
   "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"

};


  // 1. Fetch Leads
  const fetchLeads = async (pageNumber: number) => {
    setLoading(true);
    setError(null);
    try {
      const authToken = localStorage.getItem('token');
      const response = await fetch(
        `https://www.ayushvatika.com/api/admin/leads?page=${pageNumber}&size=10`,
        // `http://localhost:8484/api/admin/leads?page=${pageNumber}&size=10`,
        {
          headers: {
            'Authorization': `Bearer ${authToken}`,
          },
        }
      );

      if (!response.ok) throw new Error('Failed to fetch leads.');

      const data = await response.json();
      setLeads(data.content);
      setTotalPages(data.totalPages);
      setPage(data.number);
      setSelectedLeadIds([]);
    } catch (err) {
      setError('Could not fetch leads. Please try again later.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // 2. Fetch Subordinates
  useEffect(() => {
    const fetchSubs = async () => {
        try {
            const response = await getSubordinatesByParent(null);
            setSubordinates(response.data);
        } catch (err) {
            console.error("Failed to fetch subordinates", err);
        }
    };
    fetchSubs();
  }, []);

  useEffect(() => {
    fetchLeads(page);
  }, [page]);

  // 3. Filter Logic
  useEffect(() => {
    let result = leads;

    if (selectedSource) {
      result = result.filter(lead => lead.source === selectedSource);
    }

    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      result = result.filter(lead => {
        const leadDate = new Date(lead.createdAt);
        return leadDate >= start && leadDate <= end;
      });
    }

    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      result = result.filter(lead =>
        lead.firstName.toLowerCase().includes(query) ||
        lead.lastName.toLowerCase().includes(query) ||
        lead.email.toLowerCase().includes(query) ||
        lead.mobile.includes(query)
      );
    }

    setFilteredLeads(result);
  }, [leads, searchQuery, selectedSource, startDate, endDate]);

  // 4. Handle Checkbox Toggle
  const handleSelectLead = (id: string) => {
    if (selectedLeadIds.includes(id)) {
      setSelectedLeadIds(selectedLeadIds.filter(leadId => leadId !== id));
    } else {
      setSelectedLeadIds([...selectedLeadIds, id]);
    }
  };

  const handleSelectAll = () => {
      if (selectedLeadIds.length === filteredLeads.length) {
          setSelectedLeadIds([]);
      } else {
          setSelectedLeadIds(filteredLeads.map(l => l.id));
      }
  };

  // 5. Handle Assignment Submit
  const handleAssignSubmit = async () => {
      if (!assignToUserId || selectedLeadIds.length === 0) {
          toast.error("Please select leads and a staff member.");
          return;
      }

      try {
          await assignLeads({
              leadIds: selectedLeadIds,
              assignToUserId: parseInt(assignToUserId)
          });
          
          toast.success(`Successfully assigned ${selectedLeadIds.length} leads!`);
          
          setSelectedLeadIds([]);
          setAssignToUserId('');
          fetchLeads(page);
      } catch (error) {
          console.error("Assignment Failed", error);
          toast.error("Failed to assign leads. Please try again.");
      }
  };

  const handlePreviousPage = () => {
    if (page > 0) setPage(page - 1);
  };

  const handleNextPage = () => {
    if (page < totalPages - 1) setPage(page + 1);
  };

  return (
    <AdminLayout>
      <div className="container mx-auto p-6 min-h-screen transition-colors duration-300 bg-gray-50 text-gray-900 dark:bg-gray-900 dark:text-white">
        
        <h2 className="text-3xl font-bold mb-6 text-green-600 dark:text-green-500">Hot Leads</h2>

        {/* ASSIGNMENT BAR */}
        {subordinates.length > 0 && (
            <div className="p-4 mb-6 rounded-lg shadow-md border flex flex-wrap gap-4 items-center transition-colors duration-300
            bg-white border-gray-200 dark:bg-gray-800 dark:border-gray-700">
                <span className="font-semibold text-gray-700 dark:text-white">
                    Assign Selected ({selectedLeadIds.length}):
                </span>
                <select 
                    className={`${inputClass} w-auto min-w-[200px]`}
                    value={assignToUserId}
                    onChange={(e) => setAssignToUserId(e.target.value)}
                >
                    <option value="">-- Select Staff --</option>
                    {subordinates.map(sub => (
                        <option key={sub.id} value={sub.id}>
                            {sub.username} ({sub.role})
                        </option>
                    ))}
                </select>
                <button 
                    onClick={handleAssignSubmit}
                    disabled={selectedLeadIds.length === 0 || !assignToUserId}
                    className={`px-4 py-2 rounded font-semibold transition shadow-md ${
                        selectedLeadIds.length > 0 && assignToUserId 
                        ? 'bg-green-600 text-white hover:bg-green-700' 
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed dark:bg-gray-700 dark:text-gray-400'
                    }`}
                >
                    Assign Leads
                </button>
            </div>
        )}

        {/* Search and Filter Controls */}
        <div className="p-4 rounded-lg shadow-md border mb-6 grid grid-cols-1 md:grid-cols-4 gap-4 transition-colors duration-300
        bg-white border-gray-200 dark:bg-gray-800 dark:border-gray-700">
          <div className="relative md:col-span-1">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-400">
              <FaSearch />
            </div>
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`${inputClass} pl-10`}
            />
          </div>

          <select
            value={selectedSource}
            onChange={(e) => setSelectedSource(e.target.value)}
            className={inputClass}
          >
            <option value="">All Sources</option>
            <option value="MANUAL">Manual</option>
            <option value="BULK">Bulk</option>
            <option value="WEBSITE_REGISTERED">Website Registered</option>
          </select>
          
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className={inputClass}
          />
          
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className={inputClass}
          />
        </div>

        {loading && <div className="text-center p-8 text-gray-600 dark:text-gray-300">Loading...</div>}
        {error && <div className="p-4 rounded-lg bg-red-100 border border-red-400 text-red-700 text-center dark:bg-red-900/20 dark:border-red-500 dark:text-red-200">{error}</div>}
        
        {!loading && !error && (
          <div className="animate-slideUp">
             <div className="rounded-lg shadow-md border overflow-hidden transition-colors duration-300
             bg-white border-gray-200 dark:bg-gray-800 dark:border-gray-700">
              <div className="overflow-x-auto custom-scrollbar">
                <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300">
                    <thead className="uppercase text-xs bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                    <tr>
                        <th className="px-5 py-4 w-12 text-center">
                            <input 
                                type="checkbox" 
                                onChange={handleSelectAll}
                                checked={filteredLeads.length > 0 && selectedLeadIds.length === filteredLeads.length}
                                className="cursor-pointer w-4 h-4 rounded text-green-600 focus:ring-green-500 bg-gray-200 border-gray-300 dark:bg-gray-700 dark:border-gray-600"
                            />
                        </th>
                        <th className="px-5 py-4">Name</th>
                        <th className="px-5 py-4">Email</th>
                        <th className="px-5 py-4">Mobile</th>
                        <th className="px-5 py-4">Source</th>
                        <th className="px-5 py-4">Assigned To</th>
                    </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {filteredLeads.map((lead) => (
                        <tr key={lead.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                        <td className="px-5 py-4 text-center">
                            <input 
                                type="checkbox" 
                                checked={selectedLeadIds.includes(lead.id)}
                                onChange={() => handleSelectLead(lead.id)}
                                className="cursor-pointer w-4 h-4 rounded text-green-600 focus:ring-green-500 bg-gray-200 border-gray-300 dark:bg-gray-700 dark:border-gray-600"
                            />
                        </td>
                        <td 
                            className="px-5 py-4 font-medium cursor-pointer text-gray-900 dark:text-white hover:text-green-600 dark:hover:text-green-400 transition-colors"
                            onClick={() => navigate(`/admin/leads/sales/${lead.id}`)}
                        >
                            {lead.firstName} {lead.lastName}
                        </td>
                        <td className="px-5 py-4">{lead.email}</td>
                        <td className="px-5 py-4">{lead.mobile}</td>
                        <td className="px-5 py-4">
                            <span className="px-2 py-1 rounded text-xs border bg-gray-100 border-gray-300 text-gray-600 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300">
                                {lead.source}
                            </span>
                        </td>
  <td className="px-5 py-4 align-top">
  {lead.assignedToUserId ? (
    (() => {
      const staff = subordinates.find(
        sub => sub.id === lead.assignedToUserId
      );

      if (!staff)
        return (
          <span className="text-xs text-gray-500">
            ID: {lead.assignedToUserId}
          </span>
        );

      return (
        <div className="flex flex-col gap-1">
          {/* Name + Role */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="px-2.5 py-1 rounded text-xs font-semibold
              bg-green-50 text-green-700 border border-green-200
              dark:bg-green-900/40 dark:text-green-300 dark:border-green-700">
              {staff.username}
            </span>

           {staff.role && (
  <span
    className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase
    ${roleColorMap[staff.role] || 
      "bg-teal-100 text-teal-600 dark:bg-teal-800 dark:text-teal-300"}`}
  >
    {staff.role.replace("_", " ")}
  </span>
)}

          </div>

          {/* Reporting Line */}
          {staff.parent && (
            <span className="text-[11px] text-gray-500 dark:text-gray-400 ml-1">
              Reports to{" "}
              <span className="font-medium text-gray-700 dark:text-gray-300">
                {staff.parent.username}
              </span>
            </span>
          )}
        </div>
      );
    })()
  ) : (
    <span className="text-gray-400 text-xs italic">Unassigned</span>
  )}
</td>

                        </tr>
                    ))}
                    {filteredLeads.length === 0 && (
                        <tr>
                        <td colSpan={6} className="text-center py-8 text-gray-500 dark:text-gray-400">
                            No leads found.
                        </td>
                        </tr>
                    )}
                    </tbody>
                </table>
              </div>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex justify-between items-center mt-6">
                <button
                  onClick={handlePreviousPage}
                  disabled={page === 0}
                  className="px-4 py-2 rounded shadow-sm border transition-all
                  bg-white text-gray-700 border-gray-300 hover:bg-gray-50 
                  dark:bg-gray-800 dark:text-white dark:border-gray-600 dark:hover:bg-gray-700 
                  disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="text-gray-600 dark:text-gray-400 text-sm">
                  Page <span className="font-bold text-gray-900 dark:text-white">{page + 1}</span> of {totalPages}
                </span>
                <button
                  onClick={handleNextPage}
                  disabled={page >= totalPages - 1}
                  className="px-4 py-2 rounded shadow-sm border transition-all
                  bg-white text-gray-700 border-gray-300 hover:bg-gray-50 
                  dark:bg-gray-800 dark:text-white dark:border-gray-600 dark:hover:bg-gray-700 
                  disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #9ca3af; border-radius: 3px; }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb { background: #374151; }
        .animate-slideUp { animation: slideUp 0.4s ease-out; }
        @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </AdminLayout>
  );
};

export default HotLeadsPage;